import { WindowService } from '../../../common/angular-shared/services/window/window.service';
import { CasMetadataNode, NoneNode, OtherNotSureNode, DiagnosisNode, DiagnosisShortNode } from './CasMetadataNode';
import { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import _ from 'lodash';
import { PolicyAdminStatus } from '../../../common/js/policyAdmin/PolicyAdminStatus';
import { CoverpathControllerInterface } from '../interfaces/controller/CoverpathControllerInterface';
import { ApplicationSectionsList, ApplicationSectionUtils } from '../classes/ApplicationSection';

export class ApplicationState {
  controller: CoverpathControllerInterface;
  topLevelQsCount: number;
  appMetadata: CasMetadataNode;
  errorNodes: Array<any> = [];
  topLevelNodes: Array<string> = [];
  topLevelQs: Array<string> = [];
  nodes: any = {};
  currentNodeSection: string;
  firstRender: boolean;
  qsAnswered: any = {};
  enabledNodesCache: any = {};
  subject: Subject<number> = new Subject<number>();
  questionTracker$: Observable<number> = this.subject.asObservable();
  percentComplete: Array<number> = [];
  totalPercentComplete: number;
  nextFocussable: string;
  lockableQuestions: Array<any> = [];

  sections: Array<string>;
  private eligibleRates;

  isMVR: boolean = false;
  isIdCheck: boolean = false;
  policyAdminStatus: PolicyAdminStatus;

  isAdvisor: boolean = false;
  isTeleInterviewerLoggedIn: boolean;

  invalidSection: {} = {};
  sectionValidationSubject: Subject<{}>;
  sectionValidationObservable$: Observable<{}>;
  sectionPropStrings: { section?: string[] } = {};

  removedSections = {};

  constructor(private windowService: WindowService, private coverpathClientOverride?: boolean) {
    this.controller = windowService.nativeWindow.controller;
    this.isAdvisor = !coverpathClientOverride && windowService.nativeWindow.amorphic.app === 'coverpath-agent';
    this.isTeleInterviewerLoggedIn = !!this.controller.isTeleInterviewerLoggedIn;
    this.sectionValidationSubject = new Subject<{}>();
    this.sectionValidationObservable$ = this.sectionValidationSubject.asObservable();
  }

  buildApplication(metadata) {
    const cpFilingType: string = this.controller.customer.getCoverpathFilingType();
    this.topLevelQsCount = 0;
    this.appMetadata = new CasMetadataNode(this.controller, '', { level: 0 });
    this.errorNodes = [];

    this.topLevelNodes = [];
    this.topLevelQs = [];

    const appQuestions = metadata['App_Questions'];

    const nodes = this.nodes;
    const nestedEntities = {}; // e.g. Diagnosis -> [child node1, child node2...]
    const nestedEntity: any = {}; // If the current context is a nested entity
    let currentSection;
    const levelToNodeMap = {};
    this.lockableQuestions = [];

    // level -> last encountered node at that level
    let isSingleRow = false;

    // flag to track if we want single row layout or not
    appQuestions.forEach((obj) => {
      let condition, showifConditions;

      if (ApplicationSectionUtils.shouldHideDueToTeleCMI(obj.section, this, cpFilingType)) {
        return;
      }
      if (!obj.exclude_display) {
        if (obj.section && obj.section !== this.currentNodeSection) {
          currentSection = this.appMetadata.addChild(
            new CasMetadataNode(this.controller, '', {
              section: obj.section,
              sectionOnEnter: obj.sectionOnEnter,
              topLevel: true,
              styleClass: obj.style_class,
              alwaysVisible: obj.always_visible,
              qText: obj.dynamic_question ? obj.dynamic_question : obj.question,
              interestedIn: obj.interested_in,
              pronounLocked: obj.pronoun_locked,
              controlType: obj.dynamic_control_type ? obj.dynamic_control_type : obj.control_type,
              notSupported: obj.not_supported_if,
            })
          );

          this.currentNodeSection = obj.section;

          // If the current row is a section, setup its CasMetadataNode and
          // return, otherwise treat the row as a question
          // and build the CasMetadataNode
          if (obj.control_type === 'section') {
            condition = obj.appears_based_on;
            showifConditions = this.constructEvalExp(condition, nodes, obj.key);
            if (showifConditions) {
              currentSection.subscribeToProps = showifConditions.subscribeToProps;
              currentSection.showifExp = showifConditions.expression;
            }
            currentSection.id = obj.key;
            currentSection.controlType = obj.control_type;
            nodes[currentSection.id] = currentSection;

            return;
          }
        }

        const entity = obj.entity;
        const name = obj.property;
        const controlType = obj.dynamic_control_type ? obj.dynamic_control_type : obj.control_type;

        if (obj.layout === 'rowend') {
          isSingleRow = false;
        }

        const options: any = {
          id: obj.key,
          qText: obj.dynamic_question ? obj.dynamic_question : obj.question,
          qTranslate: obj.questionTranslate,
          permutation: obj.permutation,
          permutationAnswers: obj.permutationAnswers,
          controlType: controlType,
          widths: obj.widths,
          level: obj.question_level,
          helpText: obj.help_text,
          pronounLocked: obj.pronoun_locked,
          type: obj.type,
          required: obj.required,
          requiredBasedOn: obj.required_based_on,
          placeholder: obj.placeholder,
          customProps: obj.custom_control_properties,
          placeHolder: obj.placeholder,
          styleClass: obj.style_class,
          layout: isSingleRow ? 'rowbody' : obj.layout || '',
          width: obj.width,
          fid: obj.fill_id,
          loadingCondition: obj.loading_condition,
          loadingConditionMessage: obj.loading_condition_message,
          inNestedEntity: false,
          relatedQuestions: obj.related_questions,
          iteratorMaxLength: obj.iterator_max_length,
          responsesAllowed: obj.responses_allowed,
          featureFlags: obj.feature_flags,
          addFuncSuffix: obj.addFuncSuffix,
          removeFuncSuffix: obj.removeFuncSuffix,
          subProperty: '',
          interestedIn: obj.interested_in,
          subtext: obj.subtext,
          notSupported: obj.not_supported_if,
          subRequiredIf: obj.sub_required_if,
          permanentlyDisabledConditional: obj.permanentlyDisabledConditional,
          iterateLinkLabel: obj.iterate_link_label,
          optionalInputControlType: obj.optional_input_control_type,
          optionalInputProperty: obj.optional_input_property,
          optionalInputPropertyType: obj.optional_input_property_type,
          optionalInputLabel: obj.optional_input_label,
          questionDescription: obj.question_description,
          modalType: obj.modal_type,
          obfuscateValueConditional: obj.obfuscateValueConditional,
          readonlyConditional: obj.readonlyConditional,
          readonlyReason: obj.readonlyReason,
          dataReliesOn: obj.data_relies_on,
          conditionalDestroyBlock: obj.conditional_destroy_block,
          tooltipDisabledOverride: obj.tooltip_disabled_override,
          customClientValidations: obj.custom_client_validations,
          isTeleCMIQuestion: obj.is_tele_cmi_question,
          isQuestionLockable: obj.is_question_lockable,
        };

        const isBasicType: boolean = [
          'radio-group',
          'select-basic',
          'text-basic',
          'text-date-long',
          'text-date-short',
          'custom-phone-number',
          'custom-ssn',
        ].includes(options.controlType);
        const isOptional: boolean = options.required === 'no';
        const hasRequiredBasedOn: boolean = !!options.requiredBasedOn;

        /**
         * This adds a "notEmpty()" client-side validation to every basic component
         * unless obj.required is set to "no" or some obj.required_based_on condition is set
         */
        if (isBasicType && !isOptional && !hasRequiredBasedOn) {
          // add "notEmpty()" as first validation to the semi-colon separated list
          const validations: string[] = (obj.client_validation && obj.client_validation.split(';')) || [];
          validations.unshift('notEmpty()');
          options.validation = _.uniq(validations).join(';');
        } else {
          options.validation = obj.client_validation;
        }

        if (obj.layout === 'rowstart') {
          isSingleRow = true;
        }

        if (obj.subproperty) {
          options.subProperty = _.compact([obj.model_path, obj.subproperty]).join('.');
        }

        let prop = _.compact([obj.model_path, obj.property]).join('.');
        condition = obj.appears_based_on;
        showifConditions = this.constructEvalExp(condition, nodes, obj.key);

        // Should this entity be cloned?
        const shouldClone = !(obj.clone && obj.clone.match(/No/i));

        // If there is a parent question, add the current question as a child
        if (!options.level) {
          // Log error?
          throw new Error(`Error with CasMetadataNode: Missing Level ${obj.key}`);
        }

        const parentNode = String(options.level) === '1' ? currentSection : levelToNodeMap[options.level - 1];
        let currNode;

        options['showifExp'] = showifConditions ? showifConditions.expression : '';
        options['subscribeToProps'] = showifConditions ? showifConditions.subscribeToProps : [];

        // Set the level
        // If parent is a checkbox, uiLevel is parent's uiLevel + 1
        // If parent is a radiogroup, uiLevel is same as parent's uiLevel
        const parentControlType = parentNode.controlType || '';
        if (obj.ui_level) {
          options.uiLevel = obj.ui_level;
        } else if (parentControlType.match(/checkbox|select/)) {
          options.uiLevel = parentNode.uiLevel + 1;
        } else if (parentControlType.match(/radio-group/)) {
          options.uiLevel = parentNode.uiLevel;
        } else if (parentControlType.match(/label/)) {
          if (options.level === '2') {
            options.uiLevel = 0;
          } else {
            options.uiLevel = parentNode.uiLevel + 1;
          }
        } else {
          options.uiLevel = parentNode.uiLevel + 1;
        }

        if (entity !== nestedEntity.name) {
          nestedEntity.name = nestedEntity.node = null;
        }

        // Nested entities require special handling
        if (nestedEntity.name && entity === nestedEntity.name) {
          if (nestedEntity.node.controlType.match(/iterate-collection/)) {
            if (entity === 'Beneficiary') {
              prop = prop.replace('currentBeneficiary', nestedEntity.node.prop + '[i]');
            }
          }

          options.inNestedEntity = true;
        }

        // OtherNotSureNode is only applicable to Checkboxes
        if (
          obj.question === 'None of the Above' ||
          obj.question === 'None of the above' ||
          obj.question === 'Neither'
        ) {
          options.parent = parentNode;
          currNode = new NoneNode(this.controller, prop, options);
        } else if (
          obj.lastCB ||
          (obj.control_type.match(/^checkbox/) &&
            obj.question &&
            obj.question !== 'Other Airlines (non-scheduled passenger service)' &&
            (obj.question.match(/^Other/) || obj.question === 'Formula Kart Experiment (FKE)'))
        ) {
          currNode = new OtherNotSureNode(this.controller, prop, options);
        } else if (obj.type === 'DiagnosisShort') {
          currNode = new DiagnosisShortNode(this.controller, prop, options);
        } else if (obj.type === 'Diagnosis') {
          currNode = new DiagnosisNode(this.controller, prop, options);
        } else {
          currNode = new CasMetadataNode(this.controller, prop, options);
        }

        currNode.section = this.currentNodeSection;
        nodes[obj.key] = currNode;

        if (options.level === '1') {
          this.topLevelNodes.push(obj.key);
          if (!condition) {
            this.topLevelQs.push(obj.key);
            this.topLevelQsCount++;
          }
        }

        // Remember the entity nodes so that they can be reused
        if (nestedEntity.name) {
          if (!nestedEntities[nestedEntity.name]) {
            nestedEntities[nestedEntity.name] = [];
          }

          if (shouldClone) {
            nestedEntities[nestedEntity.name].push(currNode);
          }

          if (nestedEntity.node.parent === parentNode) {
            currNode.resetLevel();
            nestedEntity.node.addChild(currNode, this.topLevelNodes[this.topLevelNodes.length - 1]);
          } else {
            currNode.resetLevel();
            parentNode.addChild(currNode, this.topLevelNodes[this.topLevelNodes.length - 1]);
          }
        } else {
          if (obj.type) {
            let labelOverride = false,
              customLabels = [];
            if (obj.custom_labels) {
              labelOverride = true;
              customLabels = obj.custom_labels.split(',');
            }
            let nestedNodes;
            if (obj.type.match(/array *of *([A-Za-z]*)/)) {
              nestedEntity.name = RegExp.$1;
              nestedEntity.node = currNode;

              // If the nested entity is already in the hash, add all its nodes to the
              // current node (exclude Bene)
              if (shouldClone && nestedEntities[nestedEntity.name]) {
                nestedNodes = nestedEntities[nestedEntity.name];

                nestedNodes.forEach((theNode: CasMetadataNode, index) => {
                  const clone = theNode.shallowCopy();
                  const toks = theNode.prop.split('.');
                  clone.id = `${obj.key}.${theNode.id}`;
                  if (customLabels.length && customLabels[index]) {
                    clone.qText = customLabels[index];
                  }
                  if (nestedEntity.node.prop.indexOf('[i]') === -1) {
                    // after the first iteration, prop does not have loop index in array iterators..adding manually.
                    clone.setProp(`${nestedEntity.node.prop}[i].${toks[toks.length - 1]}`);
                  } else {
                    clone.setProp(`${nestedEntity.node.prop}.${toks[toks.length - 1]}`);
                  }
                  clone.showifExp = showifConditions ? showifConditions.expression : '';
                  clone.subscribeToProps = showifConditions ? showifConditions.subscribeToProps : [];
                  // Generate an id
                  currNode.addChild(clone, this.topLevelNodes[this.topLevelNodes.length - 1]);

                  clone.updateFid();
                  nodes[clone.id] = clone;
                });
              }
            } else if (!obj.type.match(/boolean|string|number|multivalue|date/i)) {
              nestedEntity.name = obj.type;
              nestedEntity.node = currNode;

              // If the nested entity is already in the hash, add all its nodes to the
              // current node
              if (shouldClone && nestedEntities[nestedEntity.name]) {
                nestedNodes = nestedEntities[nestedEntity.name];

                nestedNodes.forEach((theNode, index) => {
                  const copyNode = theNode.shallowCopy();
                  const toks = theNode.prop.split('.');
                  if (customLabels.length && customLabels[index]) {
                    copyNode.qText = customLabels[index];
                  }
                  copyNode.setProp(`${nestedEntity.node.prop}.${toks[toks.length - 1]}`);
                  copyNode.showifExp = showifConditions ? showifConditions.expression : '';
                  copyNode.subscribeToProps = showifConditions ? showifConditions.subscribeToProps : [];
                  copyNode.setupEventSubscriptions();
                  currNode.addChild(copyNode, this.topLevelNodes[this.topLevelNodes.length - 1]);
                  nodes[copyNode.id] = copyNode;
                });
              }
            }
          }

          parentNode.addChild(currNode, this.topLevelNodes[this.topLevelNodes.length - 1]);
        }

        levelToNodeMap[options.level] = currNode;

        if (controlType === 'conditional-error-message') {
          this.errorNodes.push(currNode.id);
        }

        if (this.isAdvisor && currNode.isQuestionLockable) {
          this.lockableQuestions.push(currNode);
        }
      }
    });

    const dummy = new CasMetadataNode(this.controller, '', {
      id: '-1',
      controlType: 'label-basic',
      level: 1,
    });

    nodes[dummy.id] = dummy;
    this.topLevelNodes.push(dummy.id);

    currentSection.addChild(dummy, this.topLevelNodes[this.topLevelNodes.length - 1]);

    // Dummy
    this.firstRender = true;
    this.nodes = nodes;
    // setupRX
    // setupZipValidation
    // setupMisc

    this.buildPropStringsList();
    return this.appMetadata;
  }

  constructEvalExp(condition?, nodes?, nodeId?) {
    try {
      if (!condition) {
        return;
      }

      // First split the condition into multiple questions
      // based on the presence of AND or OR
      // (only one of them can exist for now)
      let conditions = condition.split(' OR ');
      const orPresent = conditions.length > 1;

      if (!orPresent) {
        conditions = condition.split(' AND ');
      }

      const evalStrings = [];
      const subscribeToProps = [];

      conditions.forEach((current) => {
        const currResult = this.parseQuestion(current);
        const parentQ = currResult.parentQ;
        const predicate = currResult.predicate;
        const operator = currResult.operator;
        const evalExp = currResult.evalExp;
        let unaryOp, showif, regex;

        if (typeof predicate === 'string') {
          const predicateSplit = predicate.split(' or ');
          if (predicateSplit.length === 1) {
            showif = '"' + predicate + '"';
          } else {
            // Construct a regex
            regex = true;
            showif = predicateSplit.join('|');
          }
        } else {
          showif = predicate;
        }

        // Construct eval expression
        if (evalExp) {
          const evalExpStr: string = evalExp as string;
          if (this.isLaunchDarklyCondition(evalExpStr)) {
            evalStrings.push('this.controller.' + evalExpStr.split(':')[1]);
          } else {
            evalStrings.push('this.controller.customer.application.primaryPolicy.' + evalExp);
            let propToAddtoSubScribe = evalExp;
            //extract the eval prop from evalExp. for ex: insured.financials.employed is extracted from insured.financials.employed === false
            if (currResult.operator) {
              propToAddtoSubScribe = evalExp.split(currResult.operator)[0];
            }
            propToAddtoSubScribe = propToAddtoSubScribe.split('.').join('_');
            const existingProp = _.find(
              subscribeToProps,
              (prop) => prop === `customer_application_primaryPolicy_${propToAddtoSubScribe}`
            );
            if (!existingProp) {
              subscribeToProps.push(`customer_application_primaryPolicy_${propToAddtoSubScribe}`);
            }
          }
        } else if (parentQ) {
          const parent = nodes[parentQ];
          if (!parent) {
            throw Error(`Error evaluating expression ${current}. Cannot find parent ${parentQ}`);
          }
          const propName = parent.prop;
          const subscribeToProp = propName.split('.').join('_');

          const existingProp = _.find(subscribeToProps, (prop) => prop === subscribeToProp);
          if (!existingProp) {
            subscribeToProps.push(subscribeToProp);
          }

          if (regex) {
            const regexp: RegExp = new RegExp(showif);
            unaryOp = operator === '!==' ? '!' : '';
            evalStrings.push(`this.controller.${propName} && ${unaryOp}this.controller.${propName}.match(${regexp})`);
          } else {
            if (typeof showif !== 'undefined') {
              if (typeof showif === 'number') {
                // Account for the fact that some of our data model properties use Integers but the
                // data type is typed as a String when saving the value e.g. gender, beneficiaryType
                evalStrings.push(`(this.controller.${propName}${operator}${showif}
                                                  || this.controller.${propName}${operator}'${showif}')`);
              } else {
                evalStrings.push(`this.controller.${propName}${operator}${showif}`);
              }
            } else {
              if (currResult.parentQ2) {
                const parent2Prop = nodes[currResult.parentQ2].prop;
                const subscribeToParentProp = parent2Prop.split('.').join('_');
                const existingParentProp = _.find(subscribeToProps, (prop) => prop === subscribeToParentProp);
                if (!existingParentProp) {
                  subscribeToProps.push(subscribeToParentProp);
                }
                if (currResult.operator === '<' || currResult.operator === '>') {
                  evalStrings.push(`this.controller.${propName}${operator}Number(this.controller.${parent2Prop})`); // do a number comparison
                } else {
                  evalStrings.push(`this.controller.${propName}${operator}this.controller.${parent2Prop}`); //do a string comparison
                }
              } else {
                // Must be a true/false check
                unaryOp = operator === 'truthy' ? '' : '!';
                evalStrings.push(`${unaryOp}this.controller.${propName}`);
              }
            }
          }
        }
      });

      return {
        subscribeToProps: subscribeToProps,
        expression: orPresent ? evalStrings.join(' || ') : evalStrings.join(' && '),
      };
    } catch (e) {
      // Log error?
      throw e;
    }
  }

  parseQuestion = (condition?) => {
    let parentQ,
      parentQ2,
      predicate,
      evalExp,
      operator = '===';

    const isLaunchDarklyCondition: boolean = this.isLaunchDarklyCondition(condition as string);
    if (!condition.match(/^if/i) && !isLaunchDarklyCondition) {
      condition = 'if ' + condition;
    }

    if (condition.match(/if Q([0-9a-z.]+) is (.*)/i)) {
      parentQ = RegExp.$1;
      predicate = RegExp.$2;
    } else if (condition.match(/if Q([0-9a-z.]+) = Yes$/i)) {
      parentQ = RegExp.$1;
      predicate = true;
    } else if (condition.match(/if Q([0-9a-z.]+) = No$/i)) {
      parentQ = RegExp.$1;
      predicate = false;
    } else if (condition.match(/if Q([0-9a-z.]+) = Q([0-9a-z]+)/i)) {
      parentQ = RegExp.$1;
      parentQ2 = RegExp.$2;
    } else if (condition.match(/if Q([0-9a-z.]+) < Q([0-9a-z.]+)/i)) {
      parentQ = RegExp.$1;
      parentQ2 = RegExp.$2;
      operator = '<';
    } else if (condition.match(/if Q([0-9a-z.]+) != Q([0-9a-z.]+)/i)) {
      parentQ = RegExp.$1;
      parentQ2 = RegExp.$2;
      operator = '!==';
    } else if (condition.match(/if Q([0-9a-z.]+) = ([0-9]+)/i)) {
      parentQ = RegExp.$1;
      predicate = Number(RegExp.$2);
    } else if (condition.match(/if Q([0-9a-z.]+) = (.*)/i)) {
      parentQ = RegExp.$1;
      predicate = RegExp.$2;
    } else if (condition.match(/if Q([0-9a-z.]+) > (.*)/i)) {
      parentQ = RegExp.$1;
      predicate = Number(RegExp.$2);
      operator = '>';
    } else if (condition.match(/if Q([0-9a-z.]+) < (.*)/i)) {
      parentQ = RegExp.$1;
      predicate = Number(RegExp.$2);
      operator = '<';
    } else if (condition.match(/if ([0-9a-z.]+) = (.*)/i)) {
      evalExp = RegExp.$1 + '===' + RegExp.$2;
    }
    // if primaryBeneficiary[i].beneType = '1'
    else if (condition.match(/if ([\[\]0-9a-zA-Z.]+) = (.*)/i)) {
      evalExp = RegExp.$1 + '===' + RegExp.$2;
    } else if (
      condition.match(/([a-z0-9.\[\]]+\((([0-9](\.[0-9]+)?)+|(\.[0-9]+)?)(, ?((([0-9](\.[0-9]+)?)+)|(\.[0-9]+)))*\))/i)
    ) {
      /**
       * this will match a function with any number of numerical inputs ie: fn(1, 2, 3) and fn(1.2)
       * this regex is here to check against functions like: policy.insured.thisIsAFunction(123)
       * this will also match thisIsAFunction(123, 12)
       */
      evalExp = RegExp.$1;
    } else if (
      condition.match(/([a-z0-9]+\((([0-9](\.[0-9]+)?)+|(\.[0-9]+)?)(, ?((([0-9](\.[0-9]+)?)+)|(\.[0-9]+)))*\))/i)
    ) {
      /**
       * this will match a function with any number of numerical inputs ie: fn(1, 2, 3) and fn(1.2)
       */
      evalExp = RegExp.$1;
    } else if (condition.match(/if ([a-z]+\(\'[A-Za-z0-9]+\'\)$)/i)) {
      /**
       * Pass string parameter to function
       * Example: isPersonNotUSCitizenResidesInUS('insured')
       */
      evalExp = RegExp.$1;
    }
    // why is specifically this If capitalized? No other if is capitalized
    else if (condition.match(/If Q([0-9a-z.]+) and (.*)/)) {
      parentQ = RegExp.$1;
      evalExp = RegExp.$2;
      predicate = true;
    } else if (isLaunchDarklyCondition) {
      // check for launch darkly flag value on controller
      evalExp = condition;
    } else if (condition.match(/if Q([0-9a-z.]+) != (.*)/i)) {
      // E.g. If QB00014 != US
      parentQ = RegExp.$1;
      predicate = RegExp.$2;
      operator = '!==';
    } else if (condition.match(/if ([0-9a-z.]+) != (.*)/i)) {
      evalExp = RegExp.$1 + '!==' + RegExp.$2;
    } else if (condition.match(/if ([0-9a-z.]+) > (.*)/i)) {
      evalExp = RegExp.$1 + '>' + RegExp.$2;
      operator = '>';
    } else if (condition.match(/if Q([0-9a-z.]+)/i)) {
      // Truthy
      parentQ = RegExp.$1;
      operator = 'truthy';
    } else if (condition.match(/if !Q([0-9a-z.]+)/i)) {
      // Falsy
      parentQ = RegExp.$1;
      operator = 'falsy';
    }

    return {
      parentQ: parentQ,
      parentQ2: parentQ2,
      predicate: predicate,
      operator: operator,
      evalExp: evalExp,
    };
  };

  isLaunchDarklyCondition(condition: string): boolean {
    return /getLaunchDarklyFlagValue:[a-zA-z0-9]+/.test(condition);
  }

  setSections(stringOfSections) {
    if (stringOfSections) {
      this.sections = stringOfSections.split('|');
    } else {
      this.sections = [];
    }
  }

  private hasSection(section: string) {
    if (section) {
      return this.sections && this.sections.indexOf(section);
    } else {
      return false;
    }
  }

  getSectionIndex(section: string): number {
    const sections = ApplicationSectionUtils.getAllSections(this.controller.customer.getCoverpathFilingType(), this);
    if (section) {
      return sections && sections.indexOf(section);
    } else {
      return -1;
    }
  }

  getEligibleRates() {
    return this.eligibleRates;
  }

  setEligibleRates(rates) {
    this.eligibleRates = rates;
  }

  buildEnabledQuestions() {
    this.qsAnswered = {};
    this.enabledNodesCache = {};

    this.topLevelNodes.every((id) => {
      const node = this.nodes[id];
      let nodeDisabled: boolean = false;

      if (this.controller.customer.application.isTeleCMI) {
        // this is a tele interview app
        if (node.isTeleCMIQuestion && !this.isTeleInterviewerLoggedIn) {
          // tele interview question, but user is not a tele interviewer so disable question
          nodeDisabled = true;
        }
      }
      if (
        this.controller.customer.application.lockedByAdvisor &&
        this.controller.isQuestionLockEnabled &&
        !this.isAdvisor &&
        node.isQuestionLockable
      ) {
        nodeDisabled = true;
      }
      const primaryPolicy = this.controller.customer.application.primaryPolicy;
      const flags = {
        mmGiactWebDebitVerificationServiceEnabled: this.controller.mmGiactWebDebitVerificationServiceEnabled,
      };
      if (ApplicationSectionUtils.shouldDisableSection(primaryPolicy, node.section, flags)) {
        nodeDisabled = true;
      }

      const previousNode = node.findPrevLevel1Node();

      if (!previousNode) {
        if (!nodeDisabled) {
          this.enabledNodesCache[id] = true;
        }
      } else if (previousNode.isValid()) {
        if (!nodeDisabled) {
          this.enabledNodesCache[id] = true;
        }
        if (!previousNode.showifExp) {
          this.qsAnswered[previousNode.id] = true;
        }
      } else {
        delete this.qsAnswered[previousNode.id];
        return false;
      }

      return true;
    });

    this.calculateProgress();
    this.subject.next(this.totalPercentComplete);
  }

  previousSectionsComplete(section: number): boolean {
    for (let index = section - 1; index >= 0; index--) {
      if (this.percentComplete[index] !== 100) {
        return false;
      }
    }
    return true;
  }

  getFirstIncompleteSection(): number {
    return this.percentComplete.findIndex((s) => s !== 100);
  }

  calculateProgress() {
    const qsAnsweredCount = Object.keys(this.qsAnswered).length || 0;
    let percentFilled;

    if (this.invalidSection[this.currentNodeSection] !== undefined) {
      const numberOfAnsweredQuestionsWithNoErrors =
        qsAnsweredCount - this.invalidSection[this.currentNodeSection].count;
      percentFilled = Math.round((numberOfAnsweredQuestionsWithNoErrors / this.topLevelQsCount) * 100) / 100;
    } else {
      percentFilled = Math.round((qsAnsweredCount / this.topLevelQsCount) * 100) / 100;
    }

    this.totalPercentComplete = percentFilled > 1 ? 1 : percentFilled;
  }

  initPercentComplete() {
    for (let index = 0; index < this.appMetadata.children.length; index++) {
      this.percentComplete.push(0);
    }
  }

  canNavigateToNextSection(currentSection: number): boolean {
    if (this.isAdvisor) {
      return true;
    }

    const currentSectionName = ApplicationSectionUtils.getSectionNameByNumber(
      currentSection,
      this.controller.customer.getCoverpathFilingType(),
      this
    );
    const isInvalid = this.invalidSection[currentSectionName];
    const isComplete = this.percentComplete[currentSection] === 100;
    const previousSectionsComplete = this.previousSectionsComplete(currentSection);

    return isComplete && !isInvalid && previousSectionsComplete;
  }

  canNavigateToPreviousSection(currentSection: number): boolean {
    const currentSectionName = ApplicationSectionUtils.getSectionNameByNumber(
      currentSection,
      this.controller.customer.getCoverpathFilingType(),
      this
    );
    return !this.invalidSection[currentSectionName];
  }

  /**
   * This function generates lists of property strings in order of their
   * appearance in the appMetadata tree
   */
  buildPropStringsList() {
    let propStrings: string[];

    const traverseList = (nodeList: CasMetadataNode[]) => {
      if (!nodeList.length) {
        return;
      }

      const firstNode: CasMetadataNode = nodeList[0];
      if (firstNode.customProps) {
        firstNode.customProps.forEach((customProp) =>
          propStrings.push(`${firstNode.id}_${firstNode.prop}.${customProp}`)
        );
      } else {
        propStrings.push(`${firstNode.id}_${firstNode.prop}`);
      }
      traverseList(firstNode.children);
      traverseList(nodeList.slice(1));
    };

    this.appMetadata.children.forEach((section, index) => {
      propStrings = [];
      traverseList(section.children);
      this.sectionPropStrings[index] = propStrings;
    });
  }

  isAppCompleteForAgentSubmission() {
    const cpFilingType = this.controller.customer.getCoverpathFilingType();
    const sectionList = ApplicationSectionUtils.getAllSections(cpFilingType, this);
    return sectionList.every((section) => {
      const progressPercentage =
        this.percentComplete[ApplicationSectionUtils.getSectionNumberByName(section, cpFilingType, this)];
      return progressPercentage >= 100;
    });
  }

  isAppCompleteForClientSubmission() {
    const cpFilingType = this.controller.customer.getCoverpathFilingType();
    const sectionList = ApplicationSectionUtils.getAllSections(cpFilingType, this);
    if (sectionList[0] === 'Case Setup' && this.percentComplete[0] < 100) {
      return false;
    } else {
      return true;
    }
  }

  getFirstIncompleteLockedQuestionSection(): number {
    const cpFilingType = this.controller.customer.getCoverpathFilingType();
    let incompleteQuestionSectionIndex: number = Number.MAX_SAFE_INTEGER;
    const lockableQuestions = this.lockableQuestions;
    for (const question of lockableQuestions) {
      if (question.isShown() && !question.isValid()) {
        const sectionIndex = ApplicationSectionUtils.getSectionNumberByName(question.section, cpFilingType, this);
        incompleteQuestionSectionIndex =
          incompleteQuestionSectionIndex <= sectionIndex ? incompleteQuestionSectionIndex : sectionIndex;
      }
    }
    return incompleteQuestionSectionIndex;
  }

  hasAgentAnsweredAllLockedQuestions() {
    const lockableQuestions = this.lockableQuestions;
    for (const question of lockableQuestions) {
      if (question.isShown() && !question.isValid()) {
        return false;
      }
    }
    return true;
  }
}
